import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../lib/supabase";
import toast from "react-hot-toast";

// Keys for caching
export function getProductKeys() {
  const all = ["products"];
  return {
    all,
    lists: () => [...all, "list"],
    list: (filters) => [...all, "list", { filters }],
    details: () => [...all, "detail"],
    detail: (id) => [...all, "detail", id],
  };
}

// --- API Functions ---

async function fetchProducts() {
  const { data, error } = await supabase
    .from("products")
    .select("*")
    .order("created_at", { ascending: false });

  if (error) throw error;
  return data;
}

async function addProductApi(product) {
  const { id: _id, ...productData } = product;
  const { data, error } = await supabase
    .from("products")
    .insert([productData])
    .select();

  if (error) throw error;
  return data[0];
}

async function updateProductApi({ id, ...updates }) {
  const { data, error } = await supabase
    .from("products")
    .update(updates)
    .eq("id", id)
    .select();

  if (error) throw error;
  return data[0];
}

async function deleteProductApi(id) {
  const { error } = await supabase.from("products").delete().eq("id", id);
  if (error) throw error;
  return id;
}

// --- Hooks ---

export function useProductsQuery() {
  return useQuery({
    queryKey: getProductKeys().all,
    queryFn: fetchProducts,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

export function useProductMutations() {
  const queryClient = useQueryClient();

  const addProduct = useMutation({
    mutationFn: addProductApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getProductKeys().all });
      toast.success("Producto agregado exitosamente");
    },
    onError: (error) => {
      console.error("Error adding product:", error);
      toast.error(`Error al guardar: ${error.message}`);
    },
  });

  const updateProduct = useMutation({
    mutationFn: updateProductApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getProductKeys().all });
      toast.success("Producto actualizado exitosamente");
    },
    onError: (error) => {
      console.error("Error updating product:", error);
      toast.error(`Error al actualizar: ${error.message}`);
    },
  });

  const deleteProduct = useMutation({
    mutationFn: deleteProductApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getProductKeys().all });
      toast.success("Producto eliminado exitosamente");
    },
    onError: (error) => {
      console.error("Error deleting product:", error);
      toast.error("Error al eliminar el producto");
    },
  });

  return { addProduct, updateProduct, deleteProduct };
}
